import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, User } from 'lucide-react';
import { SportsIQsLogo } from '@/components/SportsIQsLogo';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { ResetPasswordForm } from '@/components/auth/ResetPasswordForm';
import { supabase } from '@/integrations/supabase/client';

const Auth = () => {
  const { signIn, signUp, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Check for password reset mode and signup mode
  const mode = searchParams.get('mode');
  const isPasswordReset = mode === 'reset-password';
  const shouldShowSignUp = mode === 'signup';
  const shouldShowSignIn = mode === 'signin';
  
  // Default to sign-in, but show sign-up if mode=signup, force sign-in if mode=signin
  const [isSignUp, setIsSignUp] = useState(shouldShowSignUp && !shouldShowSignIn);

  // Form state for login
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  
  // Form state for signup
  const [signUpForm, setSignUpForm] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '', 
    first_name: '', 
    last_name: '' 
  });

  useEffect(() => {
    if (user && !loading && !isPasswordReset) {
      // Only redirect if user navigated to auth page directly while already logged in
      // Don't redirect if they're in the middle of a login flow (form submission handles that)
      const returnTo = searchParams.get('returnTo');
      if (!returnTo) {
        // No returnTo means they came to auth page directly while logged in
        navigate('/');
      }
      // If there IS a returnTo, let the form submission handle the redirect
    }
  }, [user, loading, navigate, isPasswordReset, searchParams]);

  // Update isSignUp when mode changes
  useEffect(() => {
    if (shouldShowSignUp) {
      setIsSignUp(true);
    } else if (shouldShowSignIn) {
      setIsSignUp(false);
    }
  }, [shouldShowSignUp, shouldShowSignIn]);

  // Handle URL parameters for errors (but not confirmation, which is handled by EmailConfirmation component)
  useEffect(() => {
    const error = searchParams.get('error');

    if (error) {
      if (error === 'invalid_token') {
        toast.error('Invalid or expired confirmation link. Please request a new one.');
      } else if (error === 'confirmation_failed') {
        toast.error('Email confirmation failed. Please try again.');
      }
      // Clean URL
      window.history.replaceState({}, '', '/auth');
    }
  }, [searchParams]);

  // If this is a password reset, show the reset password form
  if (isPasswordReset) {
    return <ResetPasswordForm />;
  }

  // If showing forgot password form
  if (showForgotPassword) {
    return (
      <div className="min-h-dvh bg-cover bg-center bg-no-repeat flex items-center justify-center p-2 sm:p-4 relative overflow-hidden" style={{backgroundImage: 'url(/lovable-uploads/37f167c2-5c4f-4873-8fe8-48c70f19c890.png)'}}>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10">
          <ForgotPasswordForm onBackToLogin={() => setShowForgotPassword(false)} />
        </div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await signIn(loginForm.email, loginForm.password);
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Invalid email or password. Please check your credentials.');
        } else if (error.message.includes('Email not confirmed')) {
          toast.error('Please check your email and confirm your account before signing in.');
        } else {
          toast.error(error.message || 'Failed to sign in');
        }
      } else {
        toast.success('Successfully signed in!');
        const returnTo = searchParams.get('returnTo') || '/';
        navigate(returnTo);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (signUpForm.password !== signUpForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (signUpForm.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      // Use custom signup with email confirmation
      const { data, error } = await supabase.functions.invoke('send-confirmation-email', {
        body: {
          email: signUpForm.email,
          firstName: signUpForm.first_name,
          lastName: signUpForm.last_name,
          password: signUpForm.password,
        }
      });
      
      if (error) {
        if (error.message?.includes('User already registered') || error.message?.includes('already exists')) {
          toast.error('An account with this email already exists. Please sign in instead.');
        } else {
          toast.error(error.message || 'Failed to create account');
        }
      } else {
        toast.success('Account created successfully! Please check your email to confirm your account.');
        setIsSignUp(false);
        setSignUpForm({ email: '', password: '', confirmPassword: '', first_name: '', last_name: '' });
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-cover bg-center bg-no-repeat flex items-center justify-center p-2 sm:p-4 relative overflow-hidden" style={{backgroundImage: 'url(/lovable-uploads/37f167c2-5c4f-4873-8fe8-48c70f19c890.png)'}}>
      <div className="absolute inset-0 bg-black/40"></div>
      <Card className="w-full max-w-md relative z-10 backdrop-blur-sm bg-card/95 max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-primary/10 border-2 border-primary/20 rounded-full flex items-center justify-center">
              <SportsIQsLogo iconSize="w-6 h-6" className="text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Sports IQs
          </CardTitle>
          <p className="text-gray-600">
            {isSignUp ? 'Create your account' : 'Sign in to your account'}
          </p>
        </CardHeader>
        <CardContent>
          {isSignUp ? (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="First name"
                      className="pl-10"
                      value={signUpForm.first_name}
                      onChange={(e) => setSignUpForm({ ...signUpForm, first_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Last name"
                      className="pl-10"
                      value={signUpForm.last_name}
                      onChange={(e) => setSignUpForm({ ...signUpForm, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signupEmail">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="signupEmail"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10"
                    value={signUpForm.email}
                    onChange={(e) => setSignUpForm({ ...signUpForm, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signupPassword">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="signupPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    className="pl-10 pr-10"
                    value={signUpForm.password}
                    onChange={(e) => setSignUpForm({ ...signUpForm, password: e.target.value })}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-2 h-6 w-6 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    className="pl-10"
                    value={signUpForm.confirmPassword}
                    onChange={(e) => setSignUpForm({ ...signUpForm, confirmPassword: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-2 h-6 w-6 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  className="text-sm p-0 h-auto"
                  onClick={() => setShowForgotPassword(true)}
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Button>
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading} data-testid="submit-button">
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
          )}
          
          <div className="mt-4 text-center">
            <Button
              type="button"
              variant="link"
              className="text-sm"
              onClick={() => setIsSignUp(!isSignUp)}
            >
              {isSignUp 
                ? "Already have an account? Sign in" 
                : "Don't have an account? Sign up"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
